import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'

interface QueryKeyProps {
  isAuthed: boolean
  userId: number
}
export function getProfileQueryKey({ isAuthed, userId }: QueryKeyProps) {
  return ['profile', isAuthed, userId]
}

export function useProfile() {
  const { locale } = useI18n()
  const { isAuthed, sessionData } = useAutobidAuth()
  const { $trpc } = useNuxtApp()
  const push = usePush()

  const queryKey = computed(() =>
    getProfileQueryKey({
      isAuthed: isAuthed.value,
      userId: Number(sessionData.value?.user?.id),
    }),
  )

  const queryFn = async () => {
    if (!isAuthed.value) {
      return null
    }

    try {
      return await $trpc.webapi.getProfile.query({ locale: locale.value })
    }
    catch (error) {
      const { message, code, handled } = getError(error)

      if (!handled) {
        throw createError({ statusCode: code, statusMessage: message })
      }

      return null
    }
  }

  const revalidate = async () => {
    try {
      return await $trpc.internal.profileRevalidate.query()
    }
    catch (error) {
      const { message, handled } = getError(error)

      if (!handled) {
        push.error(message)
      }
    }
  }

  return {
    ...useQuery({
      queryKey: queryKey.value,
      queryFn,
    }),
    revalidate,
  }
}
